import Seo from "@/components/Seo";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  Globe,
  Newspaper,
  Sparkles,
  X,
  ChevronLeft,
  ChevronRight,
  Images,
  // Product badge icons
  Droplets,
  Waves,
  Truck,
  Shield,
  Lightbulb,
  Construction,
  HardHat,
  Wrench,
  Grid3x3,
  Route,
  Layers,
  TrafficCone,
  Radio,
  ClipboardCheck,
  Cog,
  Zap,
  Users,
  Award,
  GraduationCap,
  Building2,
  PaintBucket,
  Signpost,
  Cpu,
  Landmark,
  Sun,
  Package,
  Search,
  // Activity tag icons
  Video,
  FileText,
  Trophy,
  Leaf,
  Heart,
  Calendar,
  Megaphone,
  Lock,
  Briefcase,
  BadgeCheck,
  UserPlus,
  MessageSquare,
  TrendingUp,
  BookOpen,
  Camera,
  PlayCircle,
} from "lucide-react";
import { CLIENTS, getClientBySlug } from "@/data/clients";
import { CASE_STUDIES } from "@/lib/caseStudies";
import BottomCTA from "@/components/BottomCTA";

// Keyword-matched icon lookup for activity tags parsed from hiActivity strings.
// First matching pattern wins. Fallback is Sparkles.
const ACTIVITY_ICON_RULES = [
  [/video|film/i, Video],
  [/photo|imagery|shoot/i, Camera],
  [/demonstration|demo|launch event/i, PlayCircle],
  [/launch|product news|product feature|product education|product/i, Package],
  [/news|announcement|update/i, Newspaper],
  [/case stud|project stor|project case|project coverage|project news|project/i, Briefcase],
  [/technical|guidance|explainer|deep dive/i, Cog],
  [/feature|editorial|article|coverage|content|story/i, FileText],
  [/award/i, Trophy],
  [/sustainab|environment|net zero|green/i, Leaf],
  [/charity|community|team stor|people|staff/i, Heart],
  [/event|exhibition|conference|webinar/i, Calendar],
  [/contract|contract win|framework/i, BadgeCheck],
  [/safety|campaign/i, Shield],
  [/innovation|technology|automation/i, Lightbulb],
  [/training|course|qualification|learner|cpd/i, GraduationCap],
  [/recruit|vacanc|hire|workforce|labour/i, UserPlus],
  [/partner|partnership|promotion/i, Megaphone],
  [/site-security|security/i, Lock],
  [/appointment/i, Users],
  [/accreditation|standard/i, Award],
  [/testimonial|quote/i, MessageSquare],
  [/acquisition|growth|scale/i, TrendingUp],
  [/road scheme|scheme|network/i, Route],
  [/procurement/i, ClipboardCheck],
  [/trial|pilot/i, PlayCircle],
  [/knowledge|research|report|explain/i, BookOpen],
];

const getActivityIcon = (tag) => {
  for (const [re, Icon] of ACTIVITY_ICON_RULES) {
    if (re.test(tag)) return Icon;
  }
  return Sparkles;
};

// Icon name -> lucide component lookup for product badges
const BADGE_ICONS = {
  Droplets,
  Waves,
  Truck,
  Shield,
  Lightbulb,
  Construction,
  HardHat,
  Wrench,
  Grid3x3,
  Route,
  Layers,
  TrafficCone,
  Radio,
  ClipboardCheck,
  Cog,
  Zap,
  Users,
  Award,
  GraduationCap,
  Building2,
  PaintBucket,
  Signpost,
  Cpu,
  Landmark,
  Sun,
  Package,
  Search,
};

// First-party photography we hold ourselves. Prepended to the RSS-harvested
// media library on the matching client page so hero shots always appear first,
// even before the feed resolves.
const LOCAL_CLIENT_MEDIA = {
  "king-highway-products": [
    {
      src: "/images/services/photography/king-emcc-van.jpg",
      canon: "local:king-emcc-van",
      articleTitle:
        "KING EMCC Vehicles Rolled Out to Boost Safety on the Strategic Road Network",
      articleLink:
        "https://www.highwaysindustry.com/king-highway-emcc-vehicles-rolled-out-to-boost-safety/",
    },
  ],
};

export default function ClientDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const client = getClientBySlug(slug);
  const [latestFeature, setLatestFeature] = useState(null);
  const [mediaItems, setMediaItems] = useState(
    () => LOCAL_CLIENT_MEDIA[slug] || []
  );
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const touchStartX = useRef(null);

  // Fetch every HighwaysIndustry.com feature that mentions this client and
  // harvest images from each. First matching item drives the "Latest article"
  // block; every image from up to 10 matched features feeds the Media library.
  useEffect(() => {
    if (!client) return undefined;
    let cancelled = false;
    const searchTerm =
      client.name.split(/[\s(]/)[0].replace(/®/g, "").trim();
    const feedUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(
      `https://www.highwaysindustry.com/?s=${searchTerm}&feed=rss2`
    )}`;
    fetch(feedUrl)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.status !== "ok" || !Array.isArray(data.items)) return;
        const nameRe = new RegExp(
          `\\b${searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`,
          "i"
        );
        const matches = data.items.filter((it) => nameRe.test(it.title || ""));
        if (matches.length === 0) return;

        // ── Latest feature card (unchanged behaviour) ──
        const hit = matches[0];
        let heroThumb = hit.thumbnail;
        if (!heroThumb) {
          const html = hit.content || hit.description || "";
          const m = html.match(/<img[^>]+src=["'](https?:\/\/[^"'>]+)/i);
          if (m) heroThumb = m[1];
        }
        setLatestFeature({
          title: hit.title,
          link: hit.link,
          pubDate: hit.pubDate,
          thumbnail: heroThumb,
          categories: hit.categories || [],
        });

        // ── Media library harvest across up to 10 matched articles ──
        const seen = new Set();
        const media = [];
        const stripResize = (url) =>
          url.replace(/-\d{2,4}x\d{2,4}(?=\.[a-zA-Z]{3,4}(?:$|\?))/, "");
        const isJunk = (url) =>
          /(wp-includes|gravatar|pixel|tracking|1x1|spacer|blank\.gif)/i.test(url) ||
          /\.svg(\?|$)/i.test(url);

        matches.slice(0, 10).forEach((article) => {
          const html =
            (article.content || "") + " " + (article.description || "");
          if (article.thumbnail) {
            const canon = stripResize(article.thumbnail);
            if (!seen.has(canon) && !isJunk(canon)) {
              seen.add(canon);
              media.push({
                src: article.thumbnail,
                canon,
                articleTitle: article.title,
                articleLink: article.link,
              });
            }
          }
          const re = /<img[^>]+src=["'](https?:\/\/[^"'>]+)["'][^>]*>/gi;
          let m;
          while ((m = re.exec(html)) !== null) {
            const raw = m[1];
            const canon = stripResize(raw);
            if (seen.has(canon) || isJunk(canon)) continue;
            seen.add(canon);
            media.push({
              src: raw,
              canon,
              articleTitle: article.title,
              articleLink: article.link,
            });
          }
        });
        setMediaItems([
          ...(LOCAL_CLIENT_MEDIA[client.slug] || []),
          ...media,
        ].slice(0, 24));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [client]);

  // Lightbox: keyboard nav + Esc-to-close, only while open.
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const showPrev = useCallback(
    () =>
      setLightboxIdx((i) =>
        i === null ? null : (i - 1 + mediaItems.length) % mediaItems.length
      ),
    [mediaItems.length]
  );
  const showNext = useCallback(
    () =>
      setLightboxIdx((i) =>
        i === null ? null : (i + 1) % mediaItems.length
      ),
    [mediaItems.length]
  );

  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx, closeLightbox, showPrev, showNext]);


  if (!client) {
    return (
      <main className="bg-white pt-40 pb-32">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 text-center">
          <h1 className="font-heading font-black text-3xl text-slate-900">
            Client not found.
          </h1>
          <button
            onClick={() => navigate("/clients")}
            className="mt-6 text-accent-blue underline"
          >
            Back to all clients
          </button>
        </div>
      </main>
    );
  }

  // Pick the case study if the client has one wired
  const relatedCaseStudy = client.caseStudySlug
    ? CASE_STUDIES.find((cs) => cs.slug === client.caseStudySlug)
    : null;

  // Prefer a specific override URL (e.g. sub-page or dept), else use domain root
  const websiteHref = client.websiteUrl || `https://${client.domain}`;

  // Prefer scraped hero image; else fall back to case study poster; else gradient.
  const heroImg =
    client.heroImage ||
    (relatedCaseStudy && relatedCaseStudy.heroPoster) ||
    null;

  // Split the HI activity sentence into structured content-type pills.
  const activityTags = (client.hiActivity || "")
    .replace(/\.$/, "")
    .split(/,|\s+and\s+/i)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1));

  // Neighbouring clients (previous / next alphabetically)
  const currentIndex = CLIENTS.findIndex((c) => c.slug === client.slug);
  const prev = CLIENTS[currentIndex - 1];
  const next = CLIENTS[currentIndex + 1];

  // Derive a colourful hero gradient seed from client name so each client
  // gets a distinct-but-consistent hero look until real hero images arrive.
  const seed = client.name
    .split("")
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const hue = seed % 360;

  return (
    <main
      data-testid="client-detail-page"
      key={client.slug}
      className="bg-white"
    >
      <Seo
        title={`${client.name} - Highways industry client`}
        description={`${client.name}: ${client.description || "A UK highways and infrastructure specialist featured on HighwaysIndustry.com."}`}
        path={`/clients/${client.slug}`}
        image={heroImg || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": client.name,
          "url": client.domain ? `https://${client.domain}` : undefined,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": client.address,
            "postalCode": client.postcode,
            "addressCountry": "GB",
          },
          "description": client.description,
          "areaServed": "GB",
          "memberOf": {
            "@type": "Organization",
            "name": "HighwaysIndustry.com",
            "url": "https://highwaysindustry.com",
          },
        }}
      />
      {/* ─────────── Hero ─────────── */}
      <section
        data-testid="client-hero"
        className="dark-section relative overflow-hidden text-white"
        style={{
          minHeight: "72vh",
          background: heroImg
            ? "#0f172a"
            : `linear-gradient(135deg, hsl(${hue}, 70%, 22%) 0%, hsl(${(hue + 40) % 360}, 60%, 12%) 60%, #050a19 100%)`,
        }}
      >
        {/* If we have a hero image, use it as full-bleed background */}
        {heroImg && (
          <>
            <img
              src={heroImg}
              alt={client.name}
              className="absolute inset-0 w-full h-full object-cover opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950" />
          </>
        )}

        {/* Grid pattern texture */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24">
          <button
            onClick={() => navigate("/clients")}
            data-testid="back-to-clients"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-8"
          >
            <ArrowLeft size={14} />
            All clients
          </button>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-cyan-400" />
                <span className="label-eyebrow text-cyan-300">
                  Client Profile
                </span>
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                data-testid="client-name"
                className="mt-4 font-heading font-black text-5xl md:text-7xl lg:text-[7rem] leading-[0.92] tracking-tighter"
              >
                {client.name}
              </motion.h1>
              <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl">
                {client.description}
              </p>
            </div>
            <div className="lg:col-span-4 space-y-4">
              <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur p-5">
                <div className="flex items-center gap-2 text-cyan-300 text-xs uppercase tracking-widest">
                  <MapPin size={12} /> Head office
                </div>
                <div className="mt-2 text-white leading-relaxed text-sm">
                  {client.address}
                </div>
                <div className="mt-2 text-white/60 text-sm font-mono">
                  {client.postcode}
                </div>
              </div>
              <a
                href={websiteHref}
                target="_blank"
                rel="noreferrer"
                data-testid="client-website-link"
                className="flex items-center justify-between p-4 rounded-2xl border border-white/15 bg-white/5 backdrop-blur hover:border-cyan-400 hover:bg-white/10 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Globe size={16} className="text-cyan-300" />
                  <span className="text-sm text-white">{client.domain}</span>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-white/50 group-hover:text-cyan-300 transition-colors"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Why we back them ─────────── */}
      <section
        data-testid="why-we-back-them"
        className="py-20 md:py-28 bg-slate-50 border-b border-slate-200"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                About {client.name.split(" ")[0]}
              </span>
            </div>
            <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter text-slate-900">
              Why we back
              <br />
              <span className="text-accent-blue italic">{client.name}.</span>
            </h2>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {/* Product / sector badges */}
            {client.badges && client.badges.length > 0 && (
              <div
                data-testid="product-badges"
                className="flex flex-wrap gap-3 py-2"
              >
                {client.badges.map(([iconName, label]) => {
                  const Icon = BADGE_ICONS[iconName] || Cog;
                  return (
                    <span
                      key={label}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-800"
                    >
                      <Icon
                        size={16}
                        className="text-accent-blue"
                        strokeWidth={2}
                      />
                      <span className="font-medium">{label}</span>
                    </span>
                  );
                })}
              </div>
            )}

            <p className="text-slate-800 text-xl md:text-2xl leading-relaxed font-medium">
              {client.pitch ||
                `${client.name} is the kind of business the highways and infrastructure sector runs on, genuine capability, real projects, and a story worth telling. That's why they're on the platform.`}
            </p>
            <div className="pt-2">
              <a
                href={websiteHref}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-client-site"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-900 text-slate-900 font-heading font-semibold hover:bg-slate-900 hover:text-white transition-colors text-sm"
              >
                <Globe size={16} />
                Visit {client.domain}
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── How we work together ─────────── */}
      <section
        data-testid="how-we-work-together"
        className="py-20 md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                How we work together
              </span>
            </div>
            <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter text-slate-900">
              Content that puts
              <br />
              <span className="text-accent-blue italic">
                {client.name.split(" ")[0]}
              </span>{" "}
              in front of the industry.
            </h2>
          </div>

          <div className="lg:col-span-7 space-y-8">
            {/* Narrative */}
            <div className="p-6 md:p-8 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
                Our partnership
              </div>
              {client.partnership ? (
                <p className="text-slate-800 text-lg leading-relaxed">
                  {client.partnership}
                </p>
              ) : (
                <p className="text-slate-800 text-lg leading-relaxed">
                  Our editorial coverage of {client.name} includes{" "}
                  <strong>
                    {client.hiActivity
                      .replace(/\.$/, "")
                      .toLowerCase()
                      // Restore common highways-sector acronyms
                      .replace(
                        /\b(hds|ev|tfl|led|tm|its|vms|lantra|ipvs?|tmas?|scrim|uk|acl[ma])\b/gi,
                        (m) => m.toUpperCase()
                      )}
                  </strong>
                  , distributed across HighwaysIndustry.com, the sector&apos;s
                  largest independent editorial platform. Every piece is
                  written, shot and shared to reach decision-makers actively
                  working across UK highways and infrastructure.
                </p>
              )}
            </div>

            {/* What we deliver, only shown when user has provided a service map */}
            {client.hiServices && client.hiServices.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">
                  How we cover {client.name.split(" ")[0]}
                </div>
                <div
                  data-testid="activity-tags"
                  className="flex flex-wrap gap-2"
                >
                  {client.hiServices.map(([emoji, label], idx) => (
                    <span
                      key={`${label}-${idx}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-800 font-medium"
                    >
                      <span aria-hidden="true" className="text-base leading-none">
                        {emoji}
                      </span>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href={client.hiSearch}
                target="_blank"
                rel="noreferrer"
                data-testid="hi-search-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-blue text-white font-heading font-semibold hover:bg-cyan-500 transition-colors text-sm"
              >
                <Newspaper size={16} />
                See {client.name} on HighwaysIndustry.com
                <ArrowUpRight size={14} />
              </a>
              {client.featureArticle && (
                <a
                  href={client.featureArticle}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="feature-article-link"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-900 font-heading font-semibold hover:border-accent-blue hover:text-accent-blue transition-colors text-sm"
                >
                  <Sparkles size={16} />
                  Featured article
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Latest live feature ─────────── */}
      {latestFeature && (
        <section
          data-testid="latest-feature"
          className="pb-16 md:pb-20"
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Live on HighwaysIndustry.com
              </span>
            </div>
            <h2 className="mt-3 font-heading font-black text-2xl md:text-3xl tracking-tight text-slate-900">
              Latest article about {client.name.split(/[\s(]/)[0]}.
            </h2>
            <a
              href={latestFeature.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="latest-feature-link"
              className="group mt-6 flex flex-col md:flex-row gap-6 items-stretch rounded-2xl overflow-hidden border border-slate-200 bg-white hover:border-accent-blue hover:shadow-lg transition-all"
            >
              {latestFeature.thumbnail && (
                <div className="md:w-1/3 aspect-[16/10] md:aspect-auto bg-slate-100 overflow-hidden flex-shrink-0">
                  <img
                    src={`https://images.weserv.nl/?url=${encodeURIComponent(
                      latestFeature.thumbnail.replace(/^https?:\/\//, "")
                    )}&w=800&h=500&fit=cover`}
                    alt={latestFeature.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="flex-1 p-5 md:p-6 flex flex-col justify-center">
                <div className="flex items-center gap-2 flex-wrap">
                  {(latestFeature.categories || [])
                    .filter(
                      (c) => !["news", "features"].includes(c.toLowerCase())
                    )
                    .slice(0, 3)
                    .map((cat) => (
                      <span
                        key={cat}
                        className="text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue"
                      >
                        {cat.replace(/&amp;/g, "&")}
                      </span>
                    ))}
                </div>
                <h3 className="mt-2 font-heading font-bold text-xl md:text-2xl text-slate-900 group-hover:text-accent-blue transition-colors leading-snug">
                  {latestFeature.title}
                </h3>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <span className="text-xs text-slate-500">
                    {latestFeature.pubDate
                      ? new Date(
                          latestFeature.pubDate.replace(" ", "T") + "Z"
                        ).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : ""}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-heading font-semibold text-accent-blue">
                    Read on HighwaysIndustry.com
                    <ArrowUpRight
                      size={14}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>
      )}

      {/* ─────────── Media library ─────────── */}
      {mediaItems.length > 0 && (
        <section
          data-testid="media-library"
          className="pb-16 md:pb-24"
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex items-end justify-between flex-wrap gap-6">
              <div>
                <div className="flex items-center gap-3">
                  <Images size={16} className="text-accent-blue" />
                  <span className="label-eyebrow text-accent-blue">
                    From the shoots
                  </span>
                </div>
                <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tighter text-slate-900">
                  {client.name.split(/[\s(]/)[0]} media library.
                </h2>
                <p className="mt-3 text-slate-600 max-w-2xl text-sm md:text-base">
                  Imagery captured across our editorial coverage of{" "}
                  {client.name}. Click any tile to open the gallery.
                </p>
              </div>
              <span
                data-testid="media-count"
                className="text-xs uppercase tracking-widest text-slate-400"
              >
                {mediaItems.length} image
                {mediaItems.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
              {mediaItems.map((item, i) => (
                <button
                  key={item.canon}
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  data-testid={`media-thumb-${i}`}
                  className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100 border border-slate-200 hover:border-accent-blue transition-colors"
                >
                  <img
                    src={
                      item.src.startsWith("/")
                        ? item.src
                        : `https://images.weserv.nl/?url=${encodeURIComponent(
                            item.src.replace(/^https?:\/\//, "")
                          )}&w=400&h=400&fit=cover`
                    }
                    alt={item.articleTitle}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/25 transition-colors"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────── Related case study ─────────── */}
      {relatedCaseStudy && (
        <section
          data-testid="related-case-study"
          className="pb-20 md:pb-28"
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Full case study
              </span>
            </div>
            <Link
              to={`/case-studies/${relatedCaseStudy.slug}`}
              data-testid={`case-study-link-${relatedCaseStudy.slug}`}
              className="mt-4 block group relative rounded-3xl overflow-hidden border border-slate-200"
            >
              <div className="grid md:grid-cols-2">
                <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                  {relatedCaseStudy.heroPoster && (
                    <img
                      src={relatedCaseStudy.heroPoster}
                      alt={relatedCaseStudy.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  )}
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center bg-white">
                  <span className="label-eyebrow text-accent-blue">
                    {relatedCaseStudy.client}
                  </span>
                  <h3 className="mt-3 font-heading font-black text-3xl md:text-4xl text-slate-900 leading-tight group-hover:text-accent-blue transition-colors">
                    {relatedCaseStudy.cardTitle || relatedCaseStudy.clientLine1}
                  </h3>
                  {relatedCaseStudy.heroSub && (
                    <p className="mt-4 text-slate-600 leading-relaxed">
                      {relatedCaseStudy.heroSub}
                    </p>
                  )}
                  <div className="mt-6 inline-flex items-center gap-2 text-accent-blue font-heading font-semibold">
                    View the full case study
                    <ArrowUpRight size={16} />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ─────────── Prev / Next nav ─────────── */}
      <section className="border-t border-slate-200 py-10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between gap-6">
          {prev ? (
            <Link
              to={`/clients/${prev.slug}`}
              data-testid="prev-client"
              className="group flex items-center gap-3 text-slate-600 hover:text-accent-blue transition-colors"
            >
              <ArrowLeft size={16} />
              <div className="text-left">
                <div className="text-xs uppercase tracking-widest text-slate-400">
                  Previous
                </div>
                <div className="font-heading font-bold text-sm md:text-base">
                  {prev.name}
                </div>
              </div>
            </Link>
          ) : (
            <div />
          )}
          <Link
            to="/clients"
            className="text-xs uppercase tracking-widest text-slate-500 hover:text-accent-blue"
          >
            All clients
          </Link>
          {next ? (
            <Link
              to={`/clients/${next.slug}`}
              data-testid="next-client"
              className="group flex items-center gap-3 text-slate-600 hover:text-accent-blue transition-colors"
            >
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-slate-400">
                  Next
                </div>
                <div className="font-heading font-bold text-sm md:text-base">
                  {next.name}
                </div>
              </div>
              <ArrowUpRight size={16} />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </section>

      {/* ─────────── Lightbox ─────────── */}
      {lightboxIdx !== null && mediaItems[lightboxIdx] && (
        <div
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            touchStartX.current = null;
            if (Math.abs(dx) < 40) return;
            if (dx < 0) showNext();
            else showPrev();
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-5 right-5 md:top-8 md:right-8 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X size={20} />
          </button>

          {mediaItems.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                data-testid="lightbox-prev"
                aria-label="Previous image"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                data-testid="lightbox-next"
                aria-label="Next image"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <div
            className="relative w-full max-w-[1400px] px-5 md:px-16 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                mediaItems[lightboxIdx].src.startsWith("/")
                  ? mediaItems[lightboxIdx].src
                  : `https://images.weserv.nl/?url=${encodeURIComponent(
                      mediaItems[lightboxIdx].src.replace(/^https?:\/\//, "")
                    )}&w=1600`
              }
              alt={mediaItems[lightboxIdx].articleTitle}
              data-testid="lightbox-image"
              className="max-h-[80vh] w-auto max-w-full rounded-lg object-contain shadow-2xl"
            />
            <div className="mt-5 flex items-center justify-between w-full max-w-3xl gap-4 text-white">
              <a
                href={mediaItems[lightboxIdx].articleLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lightbox-article-link"
                className="group inline-flex items-center gap-2 text-sm md:text-base text-white/80 hover:text-accent-blue transition-colors"
              >
                <Newspaper size={14} />
                <span className="line-clamp-1">
                  {mediaItems[lightboxIdx].articleTitle}
                </span>
                <ArrowUpRight
                  size={14}
                  className="flex-shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <span className="text-xs font-mono tracking-widest text-white/50 flex-shrink-0">
                {lightboxIdx + 1} / {mediaItems.length}
              </span>
            </div>
          </div>
        </div>
      )}

      <BottomCTA
        headline={`Let's talk about ${client.name}.`}
        subline={`Whether you're inside ${client.name} or a business like it, HighwaysIndustry.com puts you in front of the sector that matters. Two ways in.`}
      />
    </main>
  );
}
